// Contacts-list filters (Sales -> Customers -> Contacts). The list is backed by
// a dedicated endpoint (/client-contacts), so its filters live here and are
// referenced by `activeFilters` in the `client-contact` list-builder config.

import { CLIENT_CONTACT_TYPE_OPTIONS } from '@supply-mover-2-0/shared';
import { createKeywordFilter } from './factories';
import type { FilterRegistry } from './types';

export const clientContactFilters: FilterRegistry = {
	// Keyword search on the contact's name. Backed by a simple LIKE for now;
	// intended to be replaced by an OpenSearch relevance query later (mirrors the
	// clients list's `clientSearch`).
	contactSearch: {
		...createKeywordFilter({
			label: 'Search',
			placeholder: 'Search contacts by name...',
			objectTable: 'client_contact',
			listKeys: ['client-contact'],
		}),
		queryParamName: 'search',
	},

	// Static multi-select over the fixed set of contact types. The backend
	// (ClientContactsListService) reads the `contactType` param and applies
	// `client_contact.type IN (...)`.
	contactType: {
		type: 'multi-select',
		label: 'Contact Type',
		placeholder: 'Select contact type...',
		colorScheme: 'blue',
		searchable: false,
		loadOnFocus: false,
		options: CLIENT_CONTACT_TYPE_OPTIONS.map((option) => ({
			id: option.id,
			label: option.label,
		})),
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'label' as keyof unknown,
		},
		queryParamName: 'contactType',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client-contact'],
	},

	// Typeahead over the parent client. Options are served by the existing
	// `/clients` list endpoint (same pattern as the clients list's `customers`
	// filter). The backend reads the `clientIds` param and applies
	// `client_contact.fk_client__id IN (...)`.
	contactClient: {
		type: 'multi-select',
		label: 'Client',
		placeholder: 'Type to search clients...',
		colorScheme: 'purple',
		searchable: true,
		loadOnFocus: false,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
			secondary: 'externalId' as keyof unknown,
		},
		apiEndpoint: '/clients',
		searchFilterName: 'clientSearch',
		apiParams: {
			page: '1',
			limit: '10',
			fields: JSON.stringify(['client.id', 'client.name', 'client.externalId']),
		},
		queryParamName: 'clientIds',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client-contact'],
	},

	// Typeahead over campaigns; options load from /client-contacts/campaign-options.
	// The backend (ClientContactsListService) reads the `campaigns` param and
	// keeps contacts with a campaign_membership in any of the selected campaigns.
	campaigns: {
		type: 'multi-select',
		label: 'Campaign',
		placeholder: 'Search campaigns...',
		colorScheme: 'green',
		searchable: true,
		loadOnFocus: true,
		displayFields: {
			id: 'id' as keyof unknown,
			primary: 'name' as keyof unknown,
		},
		apiEndpoint: '/client-contacts/campaign-options',
		queryParamName: 'campaigns',
		multiple: true,
		format: 'array',
		valueExtractor: {
			property: 'id',
		},
		listKeys: ['client-contact'],
	},
};
